import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import LoanPurchaseCost from "./LoanPurchaseCost";
import RentAndExpense from "./RentAndExpense";
import REIStepper from "../../../components/ui/stepper";
import { httpPost, useHttpGets } from "../../../services/httpService";
import { REIButton, REICheckBox, REIDialoge, REIProgressSpinner } from "../../../components/ui";
import { ApiEndPoints } from "../../../utils/endpoints";
import { USER_DETAIL, MEMBER_KEY, PROPERTY_TYPE } from "../../../utils/constants";
import {
  IAnalysedProperty,
  IGrowth,
  IPropertyDepreciationEstimation,
  IPropertyMarketFactor,
  IPropertyTaxRate,
} from "../../../interface/addAnalyser";
import { IMemberEntity, IPropertyAnalyserMedianRequest } from "../../../interface/analysed";
import { ILoan } from "../../../interface/addAnalyser";
import { IUserProfileDetails } from "../../../interface/userdetails";
import { formatDate } from "../../../utils/commonUtil";
import { useAnalysedPropertyContext } from "./Child/AnalysedPropertyContext";
import TaxAndMarket from "./TaxAndMarket";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import Results from "./Results";
import PropertyInfoCard from "./Child/PropertyDetails";
import MyDefaultProfile from "../../Search/Child/defaultProfile";

const useAnalysedProperty = (
  propertyAnalyserId: number | undefined,
  memberId: string,
  memberCountry: string,
  medianRequestBody?: IPropertyAnalyserMedianRequest,
  userProfile?: IUserProfileDetails
) => {
  const [analysedProperty, setAnalysedProperty] = useState<IAnalysedProperty | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [useDefaultProfile, setUseDefaultProfile] = useState(false);

  useEffect(() => {
    if (!propertyAnalyserId && userProfile) {
      setLoading(false);
      const isDefaqultProfile = medianRequestBody?.useDefault === "1";
      const initialProperty: IAnalysedProperty = {
        LoanTypeCost: {
          Loans: [
            {
              LoanId: 0,
              Name: "1st loan",
              IsPrincipal: userProfile.MortgageType.PrincipalNInterest === "1" ? 1 : 0,
              Amount: 2000,
              Length_Years: userProfile.MortgageType.LengthOfLoan,
              Length_Months: 1,
              InterestRate: userProfile.MortgageType.InterestRate,
              EstablishmentFees: userProfile.LoanCost.EstPrice,
              ValuationFees: userProfile.LoanCost.ValuationFee,
              OtherLoanCosts: userProfile.LoanCost.OtherLoanCost,
              PropertyAnalyserid: 0,
            },
          ] as ILoan[],
          PropertyOtherPurchaseExpense: {
            CapitalInvestment: userProfile.LoanCost.ImprovementFee || 0,
            OtherCost: 0,
            SolicitorsCost: userProfile.LoanCost.SolicitorFee || 1000,
            StampDuty: 0,
          },
          PropertyPurchaseCost: {
            CashDeposit: isDefaqultProfile
              ? userProfile.LoanDeposit.EnterPercentage == "1"
                ? userProfile.LoanDeposit.CashDepositPercentage
                : userProfile.LoanDeposit.CashDeposit
              : 10,
            EquityDeposit: isDefaqultProfile
              ? userProfile.LoanDeposit.EnterPercentage == "1"
                ? userProfile.LoanDeposit.EquityDepositPercentage
                : userProfile.LoanDeposit.EquityDeposit || 0
              : 0,
            MarketValueNow: undefined,
            PurchaseAmount: undefined,
            isEnterPercentage: isDefaqultProfile ? userProfile.LoanDeposit.EnterPercentage : "1",
            isEnterValue: isDefaqultProfile ? userProfile.LoanDeposit.EnterValue : "0",
          },
        },
        PropertyDetail: {
          BathroomId: Number(medianRequestBody?.BathroomId) || 0,
          BedroomId: Number(medianRequestBody?.BedroomId) || 0,
          CarportId: Number(medianRequestBody?.CarportId || 0),
          CountryId: Number(medianRequestBody?.CountryId) || 0,
          DateAdded: medianRequestBody?.DateAdded || new Date().toLocaleDateString("en-GB"),
          Landsize: medianRequestBody?.Landsize || "0",
          MemberPropertyId:
            medianRequestBody?.MemberPropertyId != null ? medianRequestBody.MemberPropertyId.toString() : "0",
          ModulesId: medianRequestBody?.ModulesId || "0",
          PropertyDetailId: medianRequestBody?.PropertyDetailId || 0,
          PropertyName: medianRequestBody?.PropertyName || "",
          PropertyTypeId: Number(medianRequestBody?.PropertyTypeId) || 0,
          UserCountryId: medianRequestBody?.UserCountryId || 0,
          district: medianRequestBody?.district || "",
          locality: medianRequestBody?.locality || "",
          postcode: medianRequestBody?.postcode || "",
          state: medianRequestBody?.state || "",
          useDefault: medianRequestBody?.useDefault || "0",
          Growth: {
            value: 0,
            description: "",
            PropertyType: "",
            StatsPostcode: "",
            StatsPostcodeLongTermGrowth: 0,
            StatsRegion: "",
            StatsRegionLongTermGrowth: 0,
            StatsSuburb: medianRequestBody?.statsSuburb || "",
            StatsSuburbLongTermGrowth: 0,
          } as IGrowth,
          MemberEntityId: isDefaqultProfile ? userProfile.DefaultEntity.MemberEntityId.toString() : "0",
          MemberProfiles: [],
          PortionOwned: isDefaqultProfile ? "100" : "0",
        },
        RentExpense: {
          PropertyAnnualExpense: {
            AgentComm: 8,
            BodyCorp: 0,
            Cleaning: 0,
            Gardening: 0,
            Insurance: 0,
            LandLease: 0,
            LettingFee: 0,
            Maintenance: 0,
            Other: 0,
            Rates: 0,
            ServiceCont: 500,
            Yr1_Expense: null,
          },
          PropertyAnnualRentalReturns: {
            NoOfWeeksOffPeak: 0,
            NoOfWeeksPeak: 0,
            NoOfWeeksShoulder: 0,
            OccupancyOffPeak: 0,
            OccupancyPeak: 0,
            OccupancyShoulder: 0,
            RateOfOccupancy: 52,
            RatePWOffPeak: 0,
            RatePWPeak: 0,
            RatePWShoulder: 0,
            WeeklyRent: 0,
            isHoliday: "0",
            isResidential: "1",
          },
        },
        TaxMarket: {
          PropertyDepreciationEstimation: {
            Quality: 0,
            NearestCapitalCity: 0,
            ConstructionYearComplete: 0,
            NZRecentRenovation: 0,
            NZEstimatedLandValue: 0,
            WBPropertyTypeId: "0",
            CalculateDepreciation: "0",
          } as IPropertyDepreciationEstimation,
          PropertyMarketFactor: {
            CapitalGrowth: userProfile.TaxRateMarketFactors.CapitalGrowth || 0,
            ConsumerPriceIndex: userProfile.TaxRateMarketFactors.ConsumerPrice || 0,
            Expense_LandTax: 0,
          } as IPropertyMarketFactor,
          PropertyTaxRate: {
            TaxRate: 0,
          } as IPropertyTaxRate,
        },
      };
      setAnalysedProperty(initialProperty);
      setUseDefaultProfile(initialProperty.PropertyDetail.useDefault === "1");
      localStorage.removeItem("annualPropertyExpense");
      return;
    }

    const fetchAnalysedProperty = async () => {
      try {
        if (!propertyAnalyserId || propertyAnalyserId <= 0) {
          setLoading(false);
          return;
        }
        const result = await useHttpGets(
          `${ApiEndPoints.analyser}/PropertyAnalyser?MemberId=${memberId}&PropertyAnalyserId=${propertyAnalyserId}&MemberCountryCode=${memberCountry}`
        );
        if (result.status === 200 && result.data) {
          const data = result.data as IAnalysedProperty;
          setAnalysedProperty(data);
          setAnalysedProperty((prevState) => {
            if (!prevState) return undefined;
            return {
              ...prevState,
              LoanTypeCost: {
                ...prevState.LoanTypeCost,
                PropertyPurchaseCost: {
                  ...prevState.LoanTypeCost.PropertyPurchaseCost,
                  EquityDeposit: 0,
                },
              },
            };
          });
          setUseDefaultProfile(data.PropertyDetail.useDefault === "1");
        }
      } catch {
        console.error("Error fetching analysed property.");
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysedProperty();
  }, [propertyAnalyserId, memberId, memberCountry, userProfile]);

  return { analysedProperty, loading, useDefaultProfile };
};

const useUserProfile = (useDefaultProfile: boolean, memberId: string, memberCountry: string, memberName: string) => {
  const [userProfile, setUserProfile] = useState<IUserProfileDetails | undefined>(undefined);
  useEffect(() => {
    const fetchUserProfile = async () => {
      const condition = useDefaultProfile
        ? `?ModulesId=${memberId}&Country=${memberCountry}&memberName=${memberName}`
        : `/${memberCountry}`;
      try {
        const result = await useHttpGets(`${ApiEndPoints.analyser}/UserDefault${condition}`);
        if (result.status === 200 && result.data) {
          setUserProfile(result.data as IUserProfileDetails);
        }
      } catch {
        console.error("Error fetching user profile.");
      }
    };

    fetchUserProfile();
  }, [useDefaultProfile, memberId, memberCountry, memberName]);

  return userProfile;
};

const useGetMedian = (medianBody: any) => {
  const [median, setMedian] = useState(null);

  useEffect(() => {
    if (!medianBody) return;
    const fetchMedianRecords = async () => {
      try {
        const result = await httpPost(`${ApiEndPoints.analyser}/GetMedians`, medianBody);
        if (result.status === 200 && result.data) {
          setMedian(result.data);
        }
      } catch (error) {
        console.error("Error fetching median records:", error);
      }
    };

    fetchMedianRecords();
  }, [medianBody]);

  return median;
};

const AddPropertyStepper: React.FC = () => {
  const [isStepOneValid, setIsStepOneValid] = useState(false);
  const [isStepTwoValid, setIsStepTwoValid] = useState(false);
  const [isStepThreeValid, setIsStepThreeValid] = useState(false);
  const location = useLocation();
  const { propertyAnalyserId, medianRequestBody, propertyName } = location.state || {};
  const memberDetail = useMemo(() => JSON.parse(localStorage.getItem(USER_DETAIL) || "{}"), []);
  const memberId = localStorage.getItem(MEMBER_KEY) || "";
  const memberName = `${memberDetail.FirstName} ${memberDetail.LastName}`;
  const [medianRequest, setMedianRequest] = useState<IPropertyAnalyserMedianRequest>();
  const { setAnalysedProperty, setUserProfile } = useAnalysedPropertyContext();
  const userProfile = useUserProfile(medianRequestBody?.useDefault == "1", memberId, memberDetail.Country, memberName);
  const [showDefaultProfile, setShowDefaultProfile] = useState<boolean>(false);

  const {
    analysedProperty,
    loading: loadingProperty,
    useDefaultProfile,
  } = useAnalysedProperty(propertyAnalyserId, memberId, memberDetail.Country, medianRequestBody, userProfile);

  let Country = analysedProperty?.PropertyDetail.CountryId == 1 ? "AU" : "NZ";
  const memeberEntity = useSelector((state: RootState) => state.analyser.memeberEntity as IMemberEntity[]);
  const [useDefault, setUseDefault] = useState(useDefaultProfile);

  const purchaseDetailsRef = useRef<any>(null);
  const rentAndExpenseRef = useRef<any>(null);

  useEffect(() => {
    if (analysedProperty) {
      setAnalysedProperty(analysedProperty);
    }
  }, [analysedProperty]);

  useEffect(() => {
    if (medianRequestBody) {
      setMedianRequest(medianRequestBody);
      setUseDefault(medianRequestBody.useDefault === "1");
    } else if (analysedProperty) {
      setMedianRequest({
        BathroomId: analysedProperty.PropertyDetail.BathroomId.toString(),
        BedroomId: analysedProperty.PropertyDetail.BedroomId.toString(),
        CarportId: analysedProperty.PropertyDetail.CarportId.toString(),
        CountryId: analysedProperty.PropertyDetail.CountryId.toString(),
        DateAdded: formatDate(analysedProperty.PropertyDetail.DateAdded),
        Landsize: analysedProperty.PropertyDetail.Landsize.toString(),
        MemberPropertyId: Number(analysedProperty.PropertyDetail.MemberPropertyId),
        ModulesId: analysedProperty.PropertyDetail.ModulesId,
        PropertyDetailId: analysedProperty.PropertyDetail.PropertyDetailId,
        PropertyName: analysedProperty.PropertyDetail.PropertyName,
        PropertyTypeId: Number(analysedProperty.PropertyDetail.PropertyTypeId).toString(),
        UserCountryId: analysedProperty.PropertyDetail.UserCountryId,
        district: analysedProperty.PropertyDetail.district,
        locality: analysedProperty.PropertyDetail.locality,
        postcode: analysedProperty.PropertyDetail.postcode,
        state: analysedProperty.PropertyDetail.state,
        useDefault: analysedProperty.PropertyDetail.useDefault?.toString() || "0",
      });
    }
  }, [medianRequestBody, analysedProperty]);

  useEffect(() => {
    if (medianRequest && medianRequestBody) {
      httpPost(`${ApiEndPoints.analyser}/GetMedians`, medianRequestBody)
        .then((response) => {
          if (response.status === 200 && response.data) {
            const median = response.data as any;
            setTimeout(() => {
              setAnalysedProperty((prevState) => {
                if (!prevState) return undefined;

                return {
                  ...prevState,
                  PropertyDetail: {
                    ...prevState.PropertyDetail,
                    Growth: {
                      PropertyType:
                        PROPERTY_TYPE.find((type) => type.type == median?.LongTermAverageGrowth?.PropertyType)?.value ||
                        "",
                      StatsPostcode: median?.LongTermAverageGrowth?.StatsPostcode,
                      StatsPostcodeLongTermGrowth: median?.LongTermAverageGrowth?.StatsPostcodeLongTermGrowth,
                      StatsRegion: median?.LongTermAverageGrowth?.StatsRegion,
                      StatsRegionLongTermGrowth: median?.LongTermAverageGrowth?.StatsRegionLongTermGrowth,
                      StatsSuburb:
                        analysedProperty?.PropertyDetail.Growth.StatsSuburb ||
                        median?.LongTermAverageGrowth?.StatsSuburb,
                      StatsSuburbLongTermGrowth: median?.LongTermAverageGrowth?.StatsSuburbLongTermGrowth,
                    },
                  },
                  TaxMarket: {
                    ...prevState.TaxMarket,
                    PropertyDepreciationEstimation: {
                      Quality: median.WBQualityTypeId,
                      NearestCapitalCity: median.WBNearestCity,
                      ConstructionYearComplete: median.ConstructionCompleteId,
                      WBPropertyTypeId: median.WBPropertyTypeId,
                      CalculateDepreciation: median.CalculateDepreciation,
                    },
                  },
                };
              });
            }, 2000);
          }
        })
        .catch((error) => {
          console.error("Error fetching median records:", error);
        });
    }
  }, [medianRequest, setAnalysedProperty]);

  const handleUpdateProperty = (updatedProperty: Partial<IAnalysedProperty>) => {
    setAnalysedProperty((prevState) => {
      if (!prevState) return undefined;

      return {
        ...prevState,
        ...updatedProperty,
      };
    });
  };

  useEffect(() => {
    if (analysedProperty?.PropertyDetail.PropertyDetailId === 0 && useDefault) {
      const condition = useDefault
        ? `?ModulesId=${memberId}&Country=${memberDetail.Country}&memberName=${memberName}`
        : `/${memberDetail.Country}`;

      try {
        useHttpGets(`${ApiEndPoints.analyser}/UserDefault${condition}`).then((result) => {
          if (result.status === 200 && result.data) {
            setUserProfile(result.data as IUserProfileDetails);
          }
        });
      } catch {
        console.error("Error fetching user profile.");
      }
    }
  }, [useDefault]);

  const steps = [
    {
      header: "Loan & purchase costs",
      content: (
        <LoanPurchaseCost
          ref={purchaseDetailsRef}
          setIsValid={setIsStepOneValid}
          updateProperty={handleUpdateProperty}
        />
      ),
      validate: () => {
        if (purchaseDetailsRef.current) {
          return purchaseDetailsRef.current.validate();
        }
        return false;
      },
    },
    {
      header: "Rent & expenses",
      content: (
        <RentAndExpense ref={rentAndExpenseRef} setIsValid={setIsStepTwoValid} updateProperty={handleUpdateProperty} />
      ),
      validate: () => {
        if (rentAndExpenseRef.current) {
          return rentAndExpenseRef.current.validate();
        }
        return false;
      },
    },
    {
      header: "Tax & market",
      content: (
        <TaxAndMarket
          setIsValid={setIsStepThreeValid}
          updateProperty={handleUpdateProperty}
          memberEntities={memeberEntity}
        />
      ),
      validate: () => isStepThreeValid,
    },
    {
      header: "Results",
      content: <Results memberEntities={memeberEntity} />,
      validate: () => true,
    },
  ];

  const hideDefaultProfile = useCallback(async () => {
    setShowDefaultProfile(false);
    try {
      const result = await useHttpGets(
        `${ApiEndPoints.analyser}/UserDefault?ModulesId=${memberId}&Country=${memberDetail.Country}&memberName=${memberName}`
      );
      if (result.status === 200 && result.data) {
        setUserProfile(result.data as IUserProfileDetails);
      }
    } catch {
      console.error("Error fetching user profile.");
    }
  }, []);

  useEffect(() => {
    if (userProfile) {
      setUserProfile(userProfile);
    }
  }, [userProfile]);

  if (loadingProperty) {
    return <REIProgressSpinner />;
  }

  return (
    <div className="flex flex-col">
      <REIDialoge
        showHeader={true}
        header={`Edit Default Profile`}
        visible={showDefaultProfile}
        handleEditClick={hideDefaultProfile}
        position="top"
        style={{ width: "60vw" }}
        customClass="edit-default-profile"
        contentComponent={<MyDefaultProfile closeDialog={hideDefaultProfile} />}
      ></REIDialoge>
      <div className=" flex flex-row flex-wrap content-center mb-4">
        <div className="w-1/3 text-center p-3">
          <h1 className="text-orange-700">Property Analyser</h1>
          {(!propertyAnalyserId || propertyAnalyserId <= 0) && <h2>Analyse a property</h2>}
          {propertyAnalyserId > 0 && <h2>Re-analysed a property</h2>}
          {(!propertyAnalyserId || propertyAnalyserId <= 0) && (
            <div className="flex w-full text-center content-center">
              <REICheckBox
                id="pstep-chk-defaultprofile"
                className="mr-1"
                label="Use my default profile for this property"
                checked={useDefault}
                onChange={(e) => {
                  setAnalysedProperty((prevState) => {
                    setUseDefault(e.checked);
                    if (!prevState) return undefined;

                    return {
                      ...prevState,
                      PropertyDetail: {
                        ...prevState.PropertyDetail,
                        useDefault: e.checked ? "1" : "0",
                      },
                    };
                  });
                }}
              />
              <REIButton
                id="pstep-btn-viewdefaultprofile"
                label="(view default profile)"
                classNames="p-button-link mt-[-10px] ml-[-10px]"
                onClick={() => setShowDefaultProfile(true)}
              />
            </div>
          )}
        </div>
        <div className="w-2/3 justify-evenly">
          <div className="w-1/2 content-end">
            <PropertyInfoCard updateProperty={handleUpdateProperty} propertyName={propertyName}></PropertyInfoCard>
          </div>
        </div>
      </div>
      <div className="text-center text-base">
        This property is in {Country}, and will use all amounts specific to {Country} other than your income and tax
        rate.
      </div>
      <div className="flex-1 w-full">
        {analysedProperty && (
          <REIStepper
            steps={steps}
            linear={true}
            centertext="Values marked with * have been added from your default profile"
            allowStepClick={propertyAnalyserId > 0}
          />
        )}
      </div>
      <div className="content-center justify-center flex m-2 align-middle gap-2">
        <span className="text-center content-center italic">What do you think of Property Analyser? </span>
        <REIButton
          id="pstep-btn-feedback"
          text
          label="Leave Feedback"
          onClick={() => {
            window.open("https://info.realestateinvestar.com.au/satisfaction-survey", "_blank");
          }}
          classNames="border-1 border-blue-200"
        ></REIButton>
      </div>
    </div>
  );
};

export default AddPropertyStepper;
